// ABOUTME: Reusable 3-column layout for Goals, Calendar Schedule, and Chores
// ABOUTME: Extracted from DashboardRedesigned.tsx for use on both parent and child dashboards

import React from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Favorite as HeartIcon,
  LocalFireDepartment as NukeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material/styles';
import { celebrationGold } from '../theme/tokens';
import GoalProgressIndicator from './GoalProgressIndicator';
import { ChildCalendarPanel } from './Calendar/ChildCalendarPanel';
import ChoreListDisplay from './ChoreListDisplay';
import { useDelayedChoreListView } from '../hooks/useDelayedChoreListView';
import { GoalProgressIndicator as GoalProgressData, ChoreListPeriodStatus } from '../types/api';
import naluV1Theme from '../theme/theme.nalu-v1';

export interface GoalWithCelebration extends GoalProgressData {
  key: string;
  isCelebrating: boolean;
}

export interface ChildContentRowProps {
  // Goals
  goals: (GoalProgressData | GoalWithCelebration)[];
  onGoalFadeComplete?: (key: string) => void;
  showNuclearReset?: boolean;
  onNuclearReset?: () => void;

  // Calendar
  childId: number;
  showCalendar?: boolean;

  // Chores
  choreStatuses: ChoreListPeriodStatus[];
  onChoreItemToggle: (listId: number, itemId: number, isComplete: boolean) => void;
  isParentView?: boolean;
  compact?: boolean;
  onChoreRefresh?: () => void;
  autoCollapseListIds?: Set<number>;
  onApprovalSuccess?: (listId: number) => void;
  highlightedListId?: number | null;
  familyTimezone?: string;

  // Historical view
  isHistoricalView?: boolean; // When true, chores are read-only and Goals show overlay
  calendarExternalDate?: Date; // Sync ChildCalendarPanel to heart calendar's selected date
  viewDateLabel?: string; // e.g. "Apr 15" — shown next to Responsibilities header

  // Navigation
  onResponsibilitiesClick?: () => void;

  // Visual treatment (issue #1900) — a per-child border color for nalu-v1's column
  // cards; falls back to a neutral tint when not provided (e.g. the standalone child
  // dashboard page, which has no sibling-index color rotation to draw from).
  childBorderColor?: string;
}

function isGoalWithCelebration(goal: GoalProgressData | GoalWithCelebration): goal is GoalWithCelebration {
  return 'isCelebrating' in goal && 'key' in goal;
}

export const ChildContentRow: React.FC<ChildContentRowProps> = ({
  goals,
  onGoalFadeComplete,
  showNuclearReset = false,
  onNuclearReset,
  childId,
  showCalendar = false,
  choreStatuses,
  onChoreItemToggle,
  isParentView = false,
  compact = false,
  onChoreRefresh,
  autoCollapseListIds,
  onApprovalSuccess,
  highlightedListId,
  familyTimezone,
  isHistoricalView = false,
  calendarExternalDate,
  viewDateLabel,
  onResponsibilitiesClick,
  childBorderColor,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  // Solid theme fill + hairline border instead of a translucent white overlay (issue #1900)
  const columnSx = isNaluV1Theme ? {
    bgcolor: 'background.default',
    borderRadius: '14px',
    border: `1px solid ${childBorderColor ?? alpha(theme.palette.text.primary, 0.15)}`,
  } : {
    bgcolor: alpha(theme.palette.common.white, 0.95),
    borderRadius: 2,
  };

  const hasGoals = goals.length > 0;
  const hasCalendar = showCalendar;
  // Newly-created pinned lists (Make It Right / Reset & Reflect / One-on-One Time) wait out a
  // grace period before they visibly insert and reorder the list (issue #1916).
  const delayedChoreStatuses = useDelayedChoreListView(choreStatuses);
  const visibleChoreStatuses = delayedChoreStatuses.filter(s => s.totalItems > 0);
  const hasChores = visibleChoreStatuses.length > 0;

  // Hide the entire row if all three columns are empty
  if (!hasGoals && !hasCalendar && !hasChores) {
    return null;
  }

  // CSS Grid with repeat(N, 1fr) gives exactly equal columns no matter which of the 3
  // sections are present — flex-basis percentages (33%/50%) plus a gap overflowed the
  // row's width (the gap is added on top), so the browser silently shrank the columns
  // out of their intended ratio instead of keeping them equal (issue #1900).
  const visibleColumnCount = [hasGoals, hasCalendar, hasChores].filter(Boolean).length;

  const handleResponsibilitiesClick = () => {
    if (onResponsibilitiesClick) {
      onResponsibilitiesClick();
    } else {
      navigate('/responsibilities');
    }
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: `repeat(${visibleColumnCount}, 1fr)` },
        gap: isNaluV1Theme ? '20px' : 2, // matches the family dashboard's mini-card gap (issue #1900)
        mt: isNaluV1Theme ? '20px' : 1,
      }}
    >
      {/* Goals Column */}
      {hasGoals && (
        <Box
          data-testid="goal-progress-container"
          sx={{
            p: 1.5,
            position: 'relative',
            ...columnSx,
          }}
        >
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography
              variant={isNaluV1Theme ? 'h6' : 'subtitle2'}
              sx={{ fontWeight: 600, fontSize: isNaluV1Theme ? '18px' : undefined, display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              {isNaluV1Theme
                ? <HeartIcon fontSize="small" sx={{ color: 'secondary.main' }} />
                : <TrophyIcon fontSize="small" color="primary" />
              } Goals
            </Typography>
            {showNuclearReset && onNuclearReset && !isHistoricalView && (
              <Tooltip title="Nuclear reset: fail all active goals and restart from zero">
                <IconButton
                  size="small"
                  onClick={onNuclearReset}
                  aria-label="Nuclear reset all goals"
                  sx={{ color: 'text.disabled', p: 0.25, '&:hover': { color: 'error.main' } }}
                >
                  <NukeIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {goals.map((goal) => {
            const celebrating = isGoalWithCelebration(goal) ? goal.isCelebrating : false;
            const key = isGoalWithCelebration(goal) ? goal.key : `goal-${goal.goalId}`;
            return (
              <GoalProgressIndicator
                key={key}
                goalProgress={goal}
                isCelebrating={celebrating}
                onFadeComplete={celebrating && onGoalFadeComplete ? () => onGoalFadeComplete(key) : undefined}
                familyTimezone={familyTimezone}
              />
            );
          })}
          {/* Historical view overlay: Goals reflect current state only */}
          {isHistoricalView && (
            <Box
              data-testid="goals-historical-overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: alpha(theme.palette.common.white, 0.75),
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', px: 1 }}>
                Goals reflect current state
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Calendar Column (admin-only while calendar integration is in testing) */}
      {hasCalendar && (
        <Box
          data-testid="calendar-panel"
          sx={{
            p: 1.5,
            ...columnSx,
          }}
        >
          <ChildCalendarPanel childId={childId} externalDate={calendarExternalDate} />
        </Box>
      )}

      {/* Chores Column */}
      {hasChores && (
        <Box
          data-testid="chores-detail"
          sx={{
            p: 1.5,
            ...columnSx,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              '&:hover': { color: 'primary.main' }
            }}
            onClick={handleResponsibilitiesClick}
          >
            <Box component="img" src="/nalu/kuleana-256.png" alt="" sx={{ width: 20, height: 20, objectFit: 'contain' }} /> Responsibilities
            {viewDateLabel && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5, fontWeight: 400 }}>
                · {viewDateLabel}
              </Typography>
            )}
          </Typography>
          {visibleChoreStatuses.map((choreList) => {
            const isHighlighted = highlightedListId === choreList.id;
            return (
              <Box
                key={choreList.id}
                id={`chore-list-${choreList.id}`}
                sx={{
                  borderRadius: isNaluV1Theme ? '14px' : 1,
                  transition: 'box-shadow 0.3s ease-in-out',
                  ...(isHighlighted && {
                    boxShadow: `0 0 20px 5px ${alpha(celebrationGold, 0.6)}`,
                    '@media (prefers-reduced-motion: no-preference)': {
                      animation: 'pulse 1s ease-in-out 2',
                      '@keyframes pulse': {
                        '0%, 100%': { boxShadow: `0 0 20px 5px ${alpha(celebrationGold, 0.6)}` },
                        '50%': { boxShadow: `0 0 30px 10px ${alpha(celebrationGold, 0.8)}` },
                      },
                    },
                  })
                }}
              >
                <ChoreListDisplay
                  choreList={choreList}
                  onItemToggle={(listId, itemId, isComplete) =>
                    onChoreItemToggle(listId, itemId, isComplete)
                  }
                  compact={compact}
                  isParentView={isParentView}
                  onRefresh={onChoreRefresh}
                  autoCollapse={autoCollapseListIds?.has(choreList.id) && choreList.isComplete}
                  familyTimezone={familyTimezone}
                  onApprovalSuccess={onApprovalSuccess}
                  isReadOnly={isHistoricalView}
                  isHighlighted={isHighlighted}
                />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
